import { useMemo } from "react";

const SMALL_THRESHOLD = 40;

// Fixed (not random-per-render) angle/radius/size for the dots riding the
// orbit ring, so the layout never shifts between re-renders.
const RING_DOTS = [
  { angle: 18, radiusPct: 1.0, size: 0.05 },
  { angle: 96, radiusPct: 0.94, size: 0.038 },
  { angle: 168, radiusPct: 1.0, size: 0.045 },
  { angle: 244, radiusPct: 0.96, size: 0.035 },
  { angle: 312, radiusPct: 1.0, size: 0.04 },
];
const LOOSE_DOTS = [
  { top: "10%", left: "78%", size: 0.03, delay: "0s" },
  { top: "82%", left: "22%", size: 0.026, delay: "1.4s" },
];

const CSSFallback = ({ size, pulse, className, testId }) => (
  <div
    className={`relative flex items-center justify-center pointer-events-none ${className}`}
    style={{ width: size, height: size }}
    data-testid={testId}
  >
    <div className={`absolute inset-0 rounded-full bg-kaizen-violet/50 blur-xl ${pulse ? "animate-glowpulse" : ""}`} />
    <div
      className={`absolute ${pulse ? "animate-breathe" : ""}`}
      style={{
        width: size * 0.72,
        height: size * 0.72,
        borderRadius: "42% 58% 65% 35% / 45% 40% 60% 55%",
        background:
          "radial-gradient(circle at 35% 28%, rgba(238,229,255,0.95), rgba(179,127,232,0.85) 38%, rgba(120,84,199,0.9) 68%, rgba(46,32,92,0.95) 100%)",
        boxShadow: "0 0 24px 6px rgba(179,127,232,0.5)",
      }}
    />
  </div>
);

// The one shared Nova presence used across the entire app: a lightweight, pure
// CSS/SVG "energy globe" -- soft concentric ripple rings, a lit wireframe sphere,
// a slow-orbiting dashed ring with a few particle dots. No WebGL, no canvas,
// zero perf cost, works identically everywhere it's dropped in.
export const NovaOrb = ({ size = 220, className = "", pulse = true, testId = "nova-orb" }) => {
  const globeSize = size * 0.38;
  const haloSize = size * 0.52;
  const orbitSize = size * 0.62;
  const ring2 = size * 0.78;
  const ring3 = size * 0.92;
  const ring4 = size;

  const ringDots = useMemo(() => {
    return RING_DOTS.map((d) => {
      const rad = (d.angle * Math.PI) / 180;
      return {
        left: `${50 + Math.cos(rad) * 50 * d.radiusPct}%`,
        top: `${50 + Math.sin(rad) * 50 * d.radiusPct}%`,
        px: Math.max(2, size * d.size),
      };
    });
  }, [size]);

  if (size < SMALL_THRESHOLD) {
    return <CSSFallback size={size} pulse={pulse} className={className} testId={testId} />;
  }

  return (
    <div
      className={`relative flex items-center justify-center pointer-events-none ${className}`}
      style={{ width: size, height: size }}
      data-testid={testId}
    >
      {/* concentric ripple rings, blending into whatever backdrop this sits on */}
      <div
        className="absolute rounded-full"
        style={{ width: ring4, height: ring4, background: "radial-gradient(circle, rgba(147,110,225,0.10), transparent 72%)" }}
      />
      <div
        className="absolute rounded-full"
        style={{ width: ring3, height: ring3, background: "radial-gradient(circle, rgba(168,120,230,0.13), transparent 70%)" }}
      />
      <div
        className={`absolute rounded-full ${pulse ? "animate-glowpulse" : ""}`}
        style={{ width: ring2, height: ring2, background: "radial-gradient(circle, rgba(191,145,240,0.18), transparent 66%)" }}
      />

      {/* halo directly behind the sphere */}
      <div
        className={`absolute rounded-full blur-md ${pulse ? "animate-glowpulse" : ""}`}
        style={{
          width: haloSize,
          height: haloSize,
          background: "radial-gradient(circle, rgba(233,215,255,0.55), rgba(179,127,232,0.28) 58%, transparent 80%)",
        }}
      />

      {/* dashed orbit ring, slow continuous spin, carries the particle dots with it */}
      <div
        className="absolute rounded-full border border-dashed border-white/25"
        style={{ width: orbitSize, height: orbitSize, animation: pulse ? "nova-orbit-spin 48s linear infinite" : "none" }}
        data-testid="nova-orbit-ring"
      >
        {ringDots.map((d, i) => (
          <span
            key={i}
            className="absolute rounded-full bg-white/85"
            style={{ width: d.px, height: d.px, left: d.left, top: d.top, transform: "translate(-50%,-50%)", boxShadow: "0 0 6px 1px rgba(255,255,255,0.7)" }}
          />
        ))}
      </div>

      {/* a couple of loose, gently twinkling particles further out for depth */}
      {LOOSE_DOTS.map((d, i) => (
        <span
          key={i}
          className="absolute rounded-full bg-white/70 animate-glowpulse"
          style={{
            width: Math.max(2, size * d.size),
            height: Math.max(2, size * d.size),
            top: d.top,
            left: d.left,
            animationDelay: d.delay,
            boxShadow: "0 0 5px 1px rgba(255,255,255,0.6)",
          }}
        />
      ))}

      {/* the living core: lit sphere with a faint wireframe-globe grid */}
      <div
        className={`relative rounded-full overflow-hidden ${pulse ? "animate-breathe" : ""}`}
        style={{
          width: globeSize,
          height: globeSize,
          background:
            "radial-gradient(circle at 34% 28%, #FFFFFF 0%, #EFE3FF 14%, #C9A3EF 42%, #8B5CD6 76%, #4B2E82 100%)",
          boxShadow: "0 0 26px 6px rgba(179,127,232,0.55), 0 0 60px 18px rgba(139,93,199,0.3)",
        }}
        data-testid="nova-globe"
      >
        <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full" preserveAspectRatio="none">
          <g fill="none" stroke="#FFFFFF" strokeWidth="0.7">
            <ellipse cx="50" cy="50" rx="48" ry="17" opacity="0.32" />
            <ellipse cx="50" cy="50" rx="48" ry="32" opacity="0.22" />
            <ellipse cx="50" cy="50" rx="17" ry="48" opacity="0.32" />
            <ellipse cx="50" cy="50" rx="32" ry="48" opacity="0.22" />
            <circle cx="50" cy="50" r="47.5" opacity="0.25" />
          </g>
        </svg>
      </div>
    </div>
  );
};
